import { Period, Periodicity, PeriodicityKey } from '@components/time-period-selector/PeriodsTypes';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { BaseTimePeriod, MainFilters, TimePeriod } from '@services/FiltersTypes';
import periodsToOptions from '../components/time-period-selector/PeriodsToOptions';
import { findDefaultFilterBySelectedCountries, getLatestPeriod, getLatestPeriodWithMatYtd } from './FilterUtils';
import { format, getWeek, getWeekYear, parse, subMonths } from 'date-fns';
import {
  CustomMonthsPeriodState,
  CustomWeeksPeriodState,
  MainFiltersState,
} from '@state/reducers/main-filter/MainFiltersReducer';

import { Option, PageType } from './pageConfig/Types';
import { PagesConfig } from './pageConfig/PagesConfigContext';
import {
  addStartingYearForCalendarQuarter,
  getCalQuarterComparisonPeriodLabel,
} from '@components/filter-selection-note/GetComparisonPeriod';
import { WarningPeriodicityOnChangeEvent } from '@components/containers/warning-periodicity/WarningPeriodicityContainer';
import { byCountry } from '@state/reducers/initial-filter/InitialFiltersPredicates';
import { getFallbackPeriodCombo } from './period-set/PeriodSetFallback';
import { PeriodCombo } from './period-set/PeriodSet';
import { Dispatch, SetStateAction } from 'react';

const DEFAULT_PRODUCTS_PERIOD = 'JANUARY 1970';
const MAT_YTD_PREFIX_LENGTH = 3;

type CustomRelativePeriod = RelativePeriod | BaseTimePeriod | string;
const WEEK = 'Week';
const MAT = 'MAT';
const YTD = 'YTD';

export interface PeriodMeta {
  currentPeriod: string;
  comparisonPeriod: string;
}

export const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// if the number passed is 0 (Jan), then it should return Dec
export const getMonthName = (month: number): string => {
  const index = month - 1;
  return index !== -1 ? months[month - 1] : months[months.length - 1];
};

export const getMonth = (month: string): number => {
  return months.findIndex((mon) => month.toUpperCase().includes(mon.toUpperCase()));
};

const isWeekFormat = (period: string): boolean => {
  const weekPattern = /^W\d{1,2} \d{4}$/;
  return weekPattern.test(period);
};

const isWeekRangeFormat = (str: string): boolean => {
  return /^W\d{1,2} \d{4}( - W\d{1,2} \d{4})?$/.test(str.trim());
};

export const isWeekRangePayloadFormat = (str: string): boolean => {
  return /^W\d{2}-\d{2} - W\d{2}-\d{2}$/.test(str.trim());
};

export const isFullWeekRangeFormat = (str: string): boolean => {
  return /^WEEK \d{1,2} \d{4} - WEEK \d{1,2} \d{4}$/.test(str.trim());
};

export const isMonthRangeFormat = (str: string): boolean => {
  return /^[A-Za-z]{3} \d{4} - [A-Za-z]{3} \d{4}$/.test(str.trim());
};

export const getPeriodKey = (type: 'focus' | 'comparison', dateTo: Date, periodicity: Periodicity) => {
  const getPickerType = (p: Periodicity) => (p === Periodicity.CustomWeeks ? 'week' : 'date');
  const formattedDateTo = isCustomWeeks(periodicity) ? formatWwyyyy(dateTo) : formatMMMyyyy(dateTo);
  return `${type}-period-${getPickerType(periodicity)}-${formattedDateTo}`;
};

/**
 * Returns a new Date object representing the Wednesday of the same week as the given date.
 *
 * Weeks are assumed to start on Sunday (day 0), so Wednesday is always the third day of the week.
 * The returned date will have its time normalized to 00:00:00.000.
 *
 * @param {Date} date - The input date from which to determine the corresponding week's Wednesday.
 * @returns {Date} A new Date object set to the Wednesday of the same week as the input.
 *
 * @example
 * // If input is Sunday, May 18, 2025:
 * getWednesdayOfWeek(new Date('2025-05-18'))
 * // returns Wednesday, May 21, 2025
 */
export const getWednesdayOfWeek = (date: Date): Date => {
  const day = date.getDay(); // 0 (Sun) to 6 (Sat)
  const diff = 3 - day; // Wednesday is day 3
  const wednesday = new Date(date);
  wednesday.setDate(date.getDate() + diff);
  wednesday.setHours(0, 0, 0, 0); // clear time
  return wednesday;
};

/**
 * Returns a `Date` object from a given period label.
 *
 * This function ASSUMES that the period label may take on the following formats:
 * 1. MMM yyyy  (e.g. Oct 2023)
 * 2. MMMM yyyy (e.g. October 2023)
 * 3. Ww yyyy (e.g. W4 2024)
 *
 * @param period - The period label.
 */
export const getDateFromPeriod = (period: string): Date => {
  if (isWeekFormat(period)) {
    const [week, year] = period.split(' ');
    const weekNumber = Number(week.replace('W', ''));
    const firstDayOfYear = new Date(Number(year), 0, 1);
    const daysOffset = (weekNumber - 1) * 7;
    return new Date(firstDayOfYear.setDate(firstDayOfYear.getDate() + daysOffset));
  } else {
    const [month, year] = period.split(' ');
    return new Date(Number(year), getMonth(month));
  }
};

export const getMonthAndYearName = (date: Date | undefined, useUTC: boolean = false): string => {
  if (!date) {
    return '';
  }

  const { month, year } = useUTC
    ? { month: date.getUTCMonth(), year: date.getUTCFullYear() }
    : { month: date.getMonth(), year: date.getFullYear() };

  return `${getMonthName(month + 1)} ${year}`;
};

export const formatPositionInYear = (positionInYear: number, periodicity: Periodicity): string => {
  if (periodicity === Periodicity.Weekly) {
    return String(positionInYear).padStart(2, '0');
  }

  return getMonthName(positionInYear);
};

export const formatPeriod = (period: Period, dateWithYear: boolean = true): string => {
  const { positionInYear, year, periodicity } = period;
  if (!positionInYear || !year) {
    return '';
  }

  if (dateWithYear) {
    return `${formatPositionInYear(positionInYear, periodicity)} ${year}`;
  }

  return `${formatPositionInYear(positionInYear, periodicity)}`;
};

export const formatFirstActivity = (firstActivity: string): string => {
  if (firstActivity?.toUpperCase().includes('BEFORE'))
    return firstActivity[0]?.toUpperCase() + firstActivity.slice(1).toLowerCase();

  const date = parse(firstActivity, 'MMMM yyyy', new Date());
  const year = date.getFullYear();

  if (year < 2001) return 'Before 2001';

  return getMonthAndYearName(date, false);
};

// gets number of weeks in the year
export const getNumberOfWeeks = (year: number) => {
  const date = new Date(year, 0, 1);
  const isLeapYear = new Date(year, 1, 29).getMonth() === 1;

  return date.getDay() === 4 || (isLeapYear && date.getDay() === 3) ? 53 : 52;
};

export const getPeriodMetaV3 = (
  period: BaseTimePeriod,
  relativePeriod: CustomRelativePeriod,
  periodType: Periodicity,
): PeriodMeta => {
  const { label, value } = period;
  if (value?.length > 0) {
    switch (periodType) {
      case Periodicity.Weekly:
        return getWeeklyPeriod(value, relativePeriod, formatWeeklyYtdMatPeriodPayload);
      case Periodicity.Monthly:
        return getMonthlyPeriod(value, relativePeriod);
      case Periodicity.RollingQuarter:
        return getQuarterPeriod(value, relativePeriod);
      case Periodicity.CalendarQuarter:
        return getCalendarQuarterPeriod(value, relativePeriod);
      case Periodicity.CustomMonths:
        return getCustomMonthsPeriod(value, relativePeriod);
      case Periodicity.CustomWeeks: {
        return getCustomWeeksPeriod(value, relativePeriod);
      }
      case Periodicity.YTD:
      case Periodicity.MAT:
        if (label) {
          return getYtdMatPeriod(label, relativePeriod, formatWeeklyYtdMatPeriodPayload);
        }
    }
  }

  return {
    currentPeriod: '',
    comparisonPeriod: '',
  };
};

/**
 * Factory method to determine which method to use based on v3 incremental loading flag.
 * Once v3 incremental loading changes has gone mainstream, REMOVE this method to straightaway
 * call getPeriodMetaV3.
 */
export const getPeriodMetaFactory = (
  period: string,
  relativePeriod: CustomRelativePeriod,
  periodType: Periodicity,
  matYtdTimePeriodLabel?: string,
  isYtdMatV3Compatible: boolean = true,
) => {
  const p = period ?? '';
  if (isYtdMatV3Compatible) {
    return getPeriodMetaV3({ label: p, value: p, lastUpdatedDate: 0 }, relativePeriod, periodType);
  }
  return getPeriodMeta(p, relativePeriod, periodType, matYtdTimePeriodLabel);
};

/**
 * This method will return periodMeta which consist of current period and comparison period
 * Example result is {"period": "Jan 2023 - Mar 2023", "comparisonPeriod": "Jan 2022 - Mar 2022"}
 *
 * Once v3 incremental loading changes has gone mainstream, may consider to REMOVE this method.
 */
export const getPeriodMeta = (
  period: string,
  relativePeriod: CustomRelativePeriod,
  periodType: Periodicity,
  matYtdTimePeriodLabel?: string,
): PeriodMeta | undefined => {
  if (periodType === Periodicity.RollingQuarter) {
    return getQuarterPeriod(period, relativePeriod);
  }
  if (periodType === Periodicity.CalendarQuarter) {
    return getCalendarQuarterPeriod(period, relativePeriod);
  }
  if (periodType === Periodicity.Monthly) {
    return getMonthlyPeriod(period, relativePeriod);
  }
  if (periodType === Periodicity.Weekly) {
    return getWeeklyPeriod(period, relativePeriod);
  }
  if (periodType === Periodicity.CustomMonths) {
    return getCustomMonthsPeriod(period, relativePeriod);
  }
  if (periodType === Periodicity.CustomWeeks) {
    return getCustomWeeksPeriod(period, relativePeriod);
  }
  if (isPeriodicityMATOrYTD(periodType) && matYtdTimePeriodLabel) {
    return getYtdMatPeriod(matYtdTimePeriodLabel, relativePeriod);
  }
};

const getCustomMonthsPeriod = (period: string, relativePeriod: CustomRelativePeriod) => {
  /*
   * Cases:
   * 1. RelativePeriod enums, return directly
   * 2. string, return directly
   * 3. BaseTimePeriod, return label
   */
  const comparisonPeriod = extractComparisonPeriod(relativePeriod);

  return {
    currentPeriod: period,
    comparisonPeriod: comparisonPeriod,
  };
};

const extractComparisonPeriod = (relativePeriod: CustomRelativePeriod) => {
  if (
    relativePeriod === RelativePeriod.PreviousYearCurrentPeriod ||
    relativePeriod === RelativePeriod.PreviousPeriod ||
    typeof relativePeriod === 'string'
  ) {
    return relativePeriod;
  }

  return relativePeriod.label;
};

const getCustomWeeksPeriod = (period: string, relativePeriod: CustomRelativePeriod) => {
  /*
   * Cases:
   * 1. RelativePeriod enums, return directly
   * 2. string, return directly
   * 3. BaseTimePeriod, return label
   */
  const comparisonPeriod = extractComparisonPeriod(relativePeriod);

  return {
    currentPeriod: formatCustomWeeksPeriodPayload(period),
    comparisonPeriod: isWeekRangeFormat(comparisonPeriod)
      ? formatCustomWeeksPeriodPayload(comparisonPeriod)
      : comparisonPeriod,
  };
};

/**
 * Formats YTD/MAT periods payload.
 *
 * @param label - The label of the YTD/MAT period (e.g. YTD Jan 2023 - Mar 2023)
 * @param relativePeriod - The relative period (e.g. PreviousYearCurrentPeriod)
 * @param transformYtdMatFormatCallback - An optional callback to transform YTD/MAT format to its V3 incremental loading format. Once v3 incremental loading changes has gone mainstream, REMOVE this parameter.
 */
const getYtdMatPeriod = (
  label: string,
  relativePeriod: CustomRelativePeriod,
  transformYtdMatFormatCallback?: (period: string, periodicity: PeriodicityKey.YTD | PeriodicityKey.MAT) => string,
): PeriodMeta => {
  return label.includes(WEEK)
    ? getWeeklyPeriod(label, relativePeriod, transformYtdMatFormatCallback)
    : getMonthlyPeriod(label, relativePeriod);
};

export const getCalendarQuarterPeriod = (period: string, relativePeriod: CustomRelativePeriod): PeriodMeta => {
  // This object is generated to satisfy the input parameters of getCalQuarterComparisonPeriodLabel.
  // It is encouraged not to save it inside any state/redux
  const baseTimePeriod: BaseTimePeriod = {
    label: period,
    value: period,
    lastUpdatedDate: 0,
  };
  const relativePeriodCast: RelativePeriod =
    RelativePeriod.PreviousPeriod === relativePeriod
      ? RelativePeriod.PreviousPeriod
      : RelativePeriod.PreviousYearCurrentPeriod;

  const comparisonPeriod: string = getCalQuarterComparisonPeriodLabel(relativePeriodCast, baseTimePeriod);

  return {
    currentPeriod: addStartingYearForCalendarQuarter(period),
    comparisonPeriod: addStartingYearForCalendarQuarter(comparisonPeriod),
  };
};

/**
 * Converts to weekly period database format.
 * For example: Week 10 2023 => W10-23
 *
 * @param week - The week period (e.g. Week 10 2023)
 */
const convertToWeeklyPeriodDatabaseFormat = (week: string) => {
  const weekPeriodTokens = week.split(' ');
  const weekNum = weekPeriodTokens[1]?.padStart(2, '0') ?? '';
  const year = weekPeriodTokens[2]?.substring(2) ?? '';

  return `W${weekNum}-${year}`;
};

/**
 * Converts to custom weeks period database format.
 * For example: W5 2023 - W6 2023 => W05-23 - W06-23
 *
 * @param weekRange - The week range period (e.g. W5 2023 - W6 2023)
 */
export const formatCustomWeeksPeriodPayload = (weekRange: string) => {
  const rangeParts = weekRange.split(' - '); // ["W2 2024", "W5 2024"]

  const convert = (week: string) => {
    const [weekPart, yearPart] = week.split(' '); // ["W2", "2024"]
    const weekNum = weekPart.slice(1).padStart(2, '0'); // "2" -> "02"
    const year = yearPart.slice(2); // "2024" -> "24"
    return `W${weekNum}-${year}`;
  };

  const start = convert(rangeParts[0]);
  const end = convert(rangeParts[1]);

  return `${start} - ${end}`;
};

/**
 * Derives the weekly comparison period based on the selected period and its comparison period.
 * For example: Week 10 2023, PreviousYearCurrentPeriod => Week 10 2022
 *
 * @param week - The week period (e.g. Week 10 2023)
 * @param relativePeriod - The relative period (e.g. PreviousYearCurrentPeriod)
 */
const getWeeklyComparisonPeriod = (week: string, relativePeriod: RelativePeriod) => {
  const weekPeriodTokens = week.split(' ');
  const weekNum = +weekPeriodTokens[1];
  const year = +weekPeriodTokens[2];

  if (relativePeriod === RelativePeriod.PreviousYearCurrentPeriod) {
    return `Week ${weekNum} ${year - 1}`;
  }

  let comparisonWeek = weekNum - 1;
  let comparisonYear = year;
  // Handles situation where latest week is W01, and we need to get the week from previous period
  if (weekNum === 1) {
    comparisonWeek = getNumberOfWeeks(year);
    comparisonYear = year - 1;
  }

  return `Week ${comparisonWeek.toString().padStart(2, '0')} ${comparisonYear}`;
};

/**
 * Formats YTD/MAT weekly payloads for usage within data API calls.
 * For example: YTD Week 1 2023 - Week 10 2023 => YTD W01-23 - W10-23
 *
 * @param period - The period (e.g. YTD Week 1 2023 - Week 10 2023)
 * @param periodicity - YTD or MAT periodicity
 */
export const formatWeeklyYtdMatPeriodPayload = (
  period: string,
  periodicity: PeriodicityKey.YTD | PeriodicityKey.MAT,
) => {
  const weeklyRangePeriodTokens = period.split(' - ');
  const startingPeriod = convertToWeeklyPeriodDatabaseFormat(weeklyRangePeriodTokens[0].substring(4));
  const endingPeriod = convertToWeeklyPeriodDatabaseFormat(weeklyRangePeriodTokens[1]);
  return `${periodicity} ${startingPeriod} - ${endingPeriod}`;
};

/**
 * Formats weekly period payload for usage within data API calls.
 *
 * @param period - The current period (e.g. Week 10 2023)
 * @param relativePeriod - The relative period (e.g. PreviousYearCurrentPeriod)
 * @param transformYtdMatFormatCallback - An optional callback to transform YTD/MAT format to its V3 incremental loading format. Once v3 incremental loading changes has gone mainstream, REMOVE this parameter.
 */
export const getWeeklyPeriod = (
  period: string,
  relativePeriod: CustomRelativePeriod | null,
  transformYtdMatFormatCallback?: (period: string, periodicity: PeriodicityKey.YTD | PeriodicityKey.MAT) => string,
): PeriodMeta => {
  // Special case handling when weekly period is YTD or MAT
  if (period.includes(YTD)) {
    const effectivePeriod = transformYtdMatFormatCallback?.(period, PeriodicityKey.YTD) ?? period;
    return {
      currentPeriod: effectivePeriod,
      comparisonPeriod: `${effectivePeriod} LY`,
    };
  }

  if (period.includes(MAT)) {
    const effectivePeriod = transformYtdMatFormatCallback?.(period, PeriodicityKey.MAT) ?? period;
    return {
      currentPeriod: effectivePeriod,
      comparisonPeriod: `${effectivePeriod} LY`,
    };
  }

  const effectiveRelativePeriod =
    relativePeriod && relativePeriod === RelativePeriod.PreviousPeriod
      ? RelativePeriod.PreviousPeriod
      : RelativePeriod.PreviousYearCurrentPeriod;

  return {
    currentPeriod: convertToWeeklyPeriodDatabaseFormat(period),
    comparisonPeriod: convertToWeeklyPeriodDatabaseFormat(getWeeklyComparisonPeriod(period, effectiveRelativePeriod)),
  };
};

export const getProductsMonthlyPeriod = (period: string, relativePeriod: CustomRelativePeriod | null): PeriodMeta => {
  if (period.includes(YTD) || period.includes(MAT)) {
    return {
      currentPeriod: period.toUpperCase(),
      comparisonPeriod: `${period.substring(
        0,
        MAT_YTD_PREFIX_LENGTH,
      )} ${DEFAULT_PRODUCTS_PERIOD} - ${DEFAULT_PRODUCTS_PERIOD}`,
    };
  }

  return getMonthlyPeriod(period, relativePeriod);
};

export const getMonthlyPeriod = (period: string, relativePeriod: CustomRelativePeriod | null): PeriodMeta => {
  // Special handling when monthly period is YTD or MAT
  if (period.includes(YTD)) {
    return {
      currentPeriod: period,
      comparisonPeriod: `${period} LY`,
    };
  }

  if (period.includes(MAT)) {
    return {
      currentPeriod: period,
      comparisonPeriod: `${period} LY`,
    };
  }

  const monthYear = period.split(' ');
  const month = getMonth(monthYear[0]);
  const year = Number(monthYear[1]?.slice(2));
  let comparisonPeriod = `${monthYear[0]} ${year - 1}`;

  // if the current period is Jan 2021, then previous period is Dec 2020. Hence we need to minus the year as well
  if (relativePeriod && relativePeriod === RelativePeriod.PreviousPeriod) {
    comparisonPeriod = `${getMonthName(month)} ${month === 0 ? year - 1 : year}`;
  }

  return {
    currentPeriod: `${monthYear[0]} ${year}`,
    comparisonPeriod: comparisonPeriod,
  };
};

//This method will return ranged date string for both current period and comparison period
//Received period in format MMM yyyy - MMM yyyy and relative period
//Return result in the format of MMM yyyy - MMM yyyy based on relative period

export const getQuarterPeriod = (period: string, relativePeriod: CustomRelativePeriod): PeriodMeta => {
  return {
    currentPeriod: period,
    comparisonPeriod: getRangeComparisonPeriod(period, relativePeriod),
  };
};

export const checkPeriodExist = (initialFilters: MainFilters[], countryToQuery: string, timeFrame: string): any => {
  const mainFilters = initialFilters.find((e) => e?.country === countryToQuery);
  if (timeFrame === Periodicity.Weekly) {
    return mainFilters?.timeFrame.weekly;
  } else {
    return mainFilters?.timeFrame.monthly;
  }
};

// need this to update period as we dont have a time period component on certain pages
export const updateTimePeriod = async (timeFrame: Periodicity, filters: MainFilters, setPeriod: any) => {
  const option = await periodsToOptions(timeFrame, filters.timePeriod);
  const latestPeriod: any = option?.[getLatestPeriod(option)];
  if (latestPeriod !== undefined) {
    setPeriod(latestPeriod);
  }
};

export const formatProductsPeriod = (period: string, periodType: Periodicity, isExport?: boolean) => {
  // Include checking for YTD and MAT
  if (period !== '' && !period.includes(YTD) && !period.includes(MAT) && periodType === Periodicity.Monthly) {
    const month = period.substring(0, 3);
    let year = period.substring(period.length - 4);
    if (!isExport) {
      year = period.substring(period.length - 2);
    }
    return `${month} ${year}`;
  }
  return period;
};

/**
 * check whether period.value contains YTD or MAT value, return true if it contains
 * @param period Time Period
 */
export const isPeriodYTDOrMAT = (period: BaseTimePeriod) => {
  return period.value.includes(YTD) || period.value.includes(MAT);
};

export const getTrimmedMonthName = (period: string) => 
  period.replace(/\b([A-Z][a-z]{3,})\b/g, (match) => match.substring(0, 3));

export const getInitialPeriod = (
  filters: MainFilters,
  selectedTimeFrame: Periodicity,
  selectedPeriod?: BaseTimePeriod,
): BaseTimePeriod => {
  let timePeriod;
  if (!filters) {
    return {
      label: '',
      value: '',
      lastUpdatedDate: 0,
    };
  }

  if (selectedPeriod) {
    return selectedPeriod;
  }

  if (selectedTimeFrame === Periodicity.Monthly) {
    timePeriod = filters.timePeriod.monthly[getLatestPeriod(filters.timePeriod.monthly)];
  } else {
    timePeriod = filters.timePeriod.weekly[getLatestPeriod(filters.timePeriod.weekly)];
  }

  if (timePeriod) {
    return timePeriod;
  }
  return {
    label: '',
    value: '',
    lastUpdatedDate: 0,
  };
};

/**
 * get latest period if period in page config and this only applied for product page, due to product page do not have period selection
 * @param defaultFilters defaultFilter
 * @param pagesConfig Pick pageConfig data is more up to date than redux.
 * @param responseTimeFrame timeframe for product page after checkPeriodExist validation
 * @param period An optional argument to allow the retaining of a selected period given the specified timeframe
 */
export const getCorrespondingLatestPeriod = (
  defaultFilters: MainFilters[],
  pagesConfig: PagesConfig,
  responseTimeFrame: Periodicity,
  period?: BaseTimePeriod,
): BaseTimePeriod => {
  let mainFilters = findDefaultFilterBySelectedCountries(
    pagesConfig.appliedFilters[PageType.RI_PRODUCT].selectedFilters,
    defaultFilters,
  );
  const latestPeriod = getInitialPeriod(mainFilters, responseTimeFrame);

  // Retain period with respect to the available timeframe
  const mainFilterByResponseTimeFrame =
    mainFilters.timePeriod[getPeriodicityKey(responseTimeFrame) as keyof TimePeriod];
  if (
    mainFilterByResponseTimeFrame &&
    latestPeriod !== period &&
    mainFilterByResponseTimeFrame.some((p) => p.label === period?.label)
  ) {
    return period as BaseTimePeriod;
  }

  return latestPeriod;
};

/*
 * Used to get the common period between the two countries if it exist. If it doesn't, return the latest period of the newly selected country
 */
export const getPeriod = (
  periodicity: Periodicity,
  defaultInitialMainFilter: MainFilters,
  mainFiltersState: MainFiltersState,
): BaseTimePeriod => {
  const periodicityKey = getPeriodicityKey(periodicity);
  // get the list of time periods
  const timePeriods: BaseTimePeriod[] = defaultInitialMainFilter.timePeriod[periodicityKey as keyof TimePeriod];

  // get the index of latest period from the list, rolling quarter and calendar quarter is 0, because first in the list is the latest period
  const latestPeriodIndex = [Periodicity.RollingQuarter, Periodicity.CalendarQuarter].includes(periodicity)
    ? 0
    : getLatestPeriod(timePeriods);

  if (mainFiltersState.timeFrame === periodicity) {
    //get the list of period range
    const periodRange = getListOfPeriodRange(latestPeriodIndex, periodicity, timePeriods);

    // check if the period in mainFilterState exist in the period range in defaultInitialMainFilter or not.
    // If yes, then return the matching period
    const existingPeriod = periodRange.find((x) => x.label === mainFiltersState.period!.label);
    if (existingPeriod) {
      return existingPeriod;
    }
  }

  // no matching periods, so return latest period
  return timePeriods[latestPeriodIndex];
};

export const getListOfPeriodRange = (
  latestPeriodIndex: number,
  periodicity: Periodicity,
  timePeriods: BaseTimePeriod[],
) => {
  switch (periodicity) {
    //for Rolling Quarter and Calendar Quarter first range always index 0, and list will always consist 3 range
    case Periodicity.RollingQuarter:
    case Periodicity.CalendarQuarter:
      return timePeriods.slice(latestPeriodIndex, 3);
    //for Monthly and Weekly will be very based on if the list consist of MAT and YTD and the latest period starts from bottom
    //Monthly - 2 due to list consist of 3, then last index is 2
    case Periodicity.Monthly:
      return timePeriods.slice(latestPeriodIndex - 2, latestPeriodIndex + 1);
    //Weekly or default - 4 due to list consist of 5, then last index is 2
    case Periodicity.Weekly:
    default:
      return timePeriods.slice(latestPeriodIndex - 4, latestPeriodIndex + 1);
  }
};

export const getPeriodicityKey = (periodicity: Periodicity) => {
  if (periodicity === Periodicity.RollingQuarter) {
    return PeriodicityKey.RollingQuarter;
  }
  if (periodicity === Periodicity.CalendarQuarter) {
    return PeriodicityKey.CalendarQuarter;
  }

  if (periodicity === Periodicity.CustomMonths) {
    return PeriodicityKey.CustomMonths;
  }

  if (periodicity === Periodicity.CustomWeeks) {
    return PeriodicityKey.CustomWeeks;
  }

  return periodicity?.toLowerCase();
};

/**
 * Get the latest period to use while restoring from a saved view/copied link
 */
export const getRestoreSaveViewLatestPeriod: (
  periodicity: Periodicity,
  timePeriods: TimePeriod,
  matYtdLabel?: string,
) => BaseTimePeriod = (periodicity, timePeriods, matYtdLabel) => {
  let periodicityKey = getPeriodicityKey(periodicity) as keyof TimePeriod;
  if (matYtdLabel) {
    periodicityKey = matYtdLabel.includes(WEEK)
      ? (getPeriodicityKey(Periodicity.Weekly) as keyof TimePeriod)
      : (getPeriodicityKey(Periodicity.Monthly) as keyof TimePeriod);
  }
  switch (periodicity) {
    case Periodicity.RollingQuarter:
    case Periodicity.CalendarQuarter:
    case Periodicity.CustomMonths:
    case Periodicity.CustomWeeks:
      // The latest period for rolling/calendar quarters is always the first element
      return timePeriods[periodicityKey][0];
    case Periodicity.Monthly:
    case Periodicity.Weekly:
      return timePeriods[periodicityKey][getLatestPeriod(timePeriods[periodicityKey])];
    case Periodicity.MAT:
    case Periodicity.YTD:
      return getLatestPeriodWithMatYtd(timePeriods.monthly, timePeriods.weekly, matYtdLabel!);
    default:
      return {
        label: '',
        value: '',
        lastUpdatedDate: 0,
      };
  }
};

/**
 * Apply the alternative periodicity and period to revert to. This is function is used primarily
 * alongside the WarningPeriodicityContainer to handle the change of periodicity.
 */
export const handleWarningPeriodicity: (
  defaultFilters: MainFilters[],
  selectedCountries: Option[],
  setTimeFrame: (periodicity: Periodicity) => void,
  setPeriod: (period: BaseTimePeriod) => void,
) => (object: WarningPeriodicityOnChangeEvent) => void =
  (defaultFilters, selectedCountries, setTimeFrame, setPeriod) => (object) => {
    let selectedCountriesIds = selectedCountries.map((country) => country.value);
    const periodicity = object.value;

    //finds the selected countries where the selected timePeriod is not null
    const mainFilters = defaultFilters.find(
      (mainFilter) =>
        selectedCountriesIds.includes(mainFilter.country) &&
        mainFilter.timeFrame[periodicity.toLowerCase() as keyof TimePeriod],
    )!;

    const timePeriods: BaseTimePeriod[] = mainFilters.timePeriod[periodicity.toLowerCase() as keyof TimePeriod];

    const currentPeriod = timePeriods[getLatestPeriod(timePeriods)];

    setTimeFrame(periodicity);
    setPeriod(currentPeriod);
  };

//This method receive string of range period and comparisonPeriodType and will return string range comparison period
//For example Jan 2022 - Mar 2022
//The comparisonPeriodType will indicates whether the range is to be created based on last year same period or previous period
export const getRangeComparisonPeriod = (rangePeriod: string, comparisonPeriodType: CustomRelativePeriod) => {
  const periodArray = rangePeriod.split(' - ');

  const startDate = constructRollingQuarterMonth(periodArray[0], comparisonPeriodType);
  const endDate = constructRollingQuarterMonth(periodArray[1], comparisonPeriodType);

  return `${startDate} - ${endDate}`;
};

/**
 *
 * @param date is the single date either start date or end date in Mmm yyyy format
 * @param relativePeriod either SamePeriodLastYear or PreviousPeriod
 * return date value in the form of Mmm yyyy string
 */
export const constructRollingQuarterMonth = (date: string, relativePeriod: CustomRelativePeriod): string => {
  const newDate = getDateFromPeriod(date);
  switch (relativePeriod) {
    case RelativePeriod.PreviousYearCurrentPeriod:
      newDate.setFullYear(newDate.getFullYear() - 1);
      break;
    case RelativePeriod.PreviousPeriod:
      newDate.setMonth(newDate.getMonth() - 3);
      break;
    default:
      break;
  }
  return newDate ? format(newDate, 'MMM yyyy') : '';
};

//This method receive string of range period and will return string range comparison period
//by subtracting one year from each end of the range
//Example: "YTD Jan 2022 - Mar 2023" becomes "YTD Jan 2021 - Mar 2022"
export const getYTDMATComparisonPeriod = (rangePeriod: string) => {
  const parts = rangePeriod.split(' - ');

  if (parts.length === 2) {
    const startYear = parts[0].slice(-4);
    const endYear = parts[1].slice(-4);

    const updatedStartYear = (parseInt(startYear, 10) - 1).toString();
    const updatedEndYear = (parseInt(endYear, 10) - 1).toString();

    return rangePeriod.replaceAll(startYear, updatedStartYear).replaceAll(endYear, updatedEndYear);
  }

  return '';
};

/**
 * check whether comparison period is valid based on current period
 * @param timeFrame selected time frame (Monthly or Weekly)
 * @param periodValue current period value
 * @param comparisonPeriod comparison period
 */
export const checkValidComparisonPeriod = (
  timeFrame: Periodicity,
  periodValue: string,
  comparisonPeriod: RelativePeriod | BaseTimePeriod,
): boolean => {
  const periodMeta = getPeriodMeta(periodValue, comparisonPeriod, timeFrame);

  // Handles the situation where an invalid/unsupported time frame is passed
  if (!periodMeta) {
    return false;
  }

  const comparisonPeriodLabel: string = periodMeta?.comparisonPeriod.toLocaleLowerCase();

  return !(
    comparisonPeriodLabel.includes('-nan') ||
    comparisonPeriodLabel.includes('week -1') ||
    comparisonPeriodLabel.includes('undefined')
  );
};

export interface UpdatePeriodParamsORPA {
  givenPeriodCombo: PeriodCombo;
  supportedPeriodicities: Periodicity[];
  pagesConfig: PagesConfig;
  appliedCountries: Option[];
  selectedCountries: Option[];
  defaultFilters: MainFilters[];
  updatePeriod: (period: BaseTimePeriod) => void;
  updatePeriodicity: (periodicity: Periodicity) => void;
  updateComparisonPeriod: (periodicity: RelativePeriod | BaseTimePeriod) => void;
  updateCustomMonthsPeriodState: (customComparisonPeriod: CustomMonthsPeriodState) => void;
  updateCustomWeeksPeriodState: (customComparisonPeriod: CustomWeeksPeriodState) => void;
  setFilters: (filters: MainFilters) => void;
  setCountryChanged: (changed: boolean) => void;
  customMonthsPeriod: CustomMonthsPeriodState;
  customWeeksPeriod: CustomWeeksPeriodState;
}

// used to get period from main filters and update related time frame states after a country change in OR or PA page
export const updatePeriodFiltersWhenCountryChange = (
  updatePeriodParamsORPA: UpdatePeriodParamsORPA,
  isMultiCountry?: boolean,
  multiCountryLatestTimeFrameState?: TimePeriod,
) => {
  const {
    givenPeriodCombo,
    supportedPeriodicities,
    pagesConfig,
    appliedCountries,
    selectedCountries,
    defaultFilters,
    updatePeriod,
    updatePeriodicity,
    updateComparisonPeriod,
    updateCustomMonthsPeriodState,
    updateCustomWeeksPeriodState,
    setFilters,
    setCountryChanged,
    customMonthsPeriod,
    customWeeksPeriod,
  } = updatePeriodParamsORPA;

  if (appliedCountries && selectedCountries.length !== 0 && selectedCountries[0]) {
    let mainFiltersList: MainFilters[] = [];
    let mainFilters = defaultFilters.find((e) => e.country === selectedCountries[0].value)!;

    let currentTimePeriodOptions = isMultiCountry
      ? multiCountryLatestTimeFrameState!
      : defaultFilters.filter(byCountry(appliedCountries[0].value))[0].timePeriod;

    if (isPeriodLabelYTDOrMAT(givenPeriodCombo.period, selectedCountries.length)) {
      const countryList = selectedCountries.map((country) => country.value);
      mainFiltersList = getMainFiltersList(givenPeriodCombo.period, defaultFilters, countryList);

      if (mainFiltersList.length > 0) {
        mainFilters = mainFiltersList[0];
        currentTimePeriodOptions = mainFilters.timePeriod;
      }
    }

    const fallbackPeriodCombo = getFallbackPeriodCombo(
      givenPeriodCombo,
      supportedPeriodicities,
      currentTimePeriodOptions,
      customMonthsPeriod,
      customWeeksPeriod,
    );

    getAndUpdatePeriod(
      getPeriodFromPeriodCombo(givenPeriodCombo, fallbackPeriodCombo),
      currentTimePeriodOptions,
      updatePeriod,
    );

    if (fallbackPeriodCombo !== null) {
      updatePeriod(fallbackPeriodCombo.period);
      updatePeriodicity(fallbackPeriodCombo.periodicity);
      updateComparisonPeriod(fallbackPeriodCombo.comparisonPeriod);
      updateCustomMonthsPeriodState(customMonthsPeriod);
      updateCustomWeeksPeriodState(customWeeksPeriod);
    }

    if (givenPeriodCombo) setFilters(mainFilters);
    if (!pagesConfig.isTimePeriodFirstLoad) {
      setCountryChanged(true);
    }
  }
};

export const getPeriodFromPeriodCombo = (givenPeriodCombo: PeriodCombo, fallbackPeriodCombo: PeriodCombo | null) => {
  return fallbackPeriodCombo ? fallbackPeriodCombo.period : givenPeriodCombo.period;
};

export const getAndUpdatePeriod = (
  period: BaseTimePeriod,
  timePeriodOptions: TimePeriod,
  updatePeriod: (period: BaseTimePeriod) => void,
) => {
  let lastUpdateDate = getLastUpdatedDateFromTimePeriod(timePeriodOptions);

  if (period.lastUpdatedDate !== lastUpdateDate) {
    updatePeriod({ ...period, lastUpdatedDate: lastUpdateDate });
  }
};

export const getLastUpdatedDateFromTimePeriod = (timePeriodOptions: TimePeriod): number => {
  return timePeriodOptions.monthly === null || timePeriodOptions.monthly.length === 0
    ? timePeriodOptions.weekly[0].lastUpdatedDate
    : timePeriodOptions.monthly[0].lastUpdatedDate;
};
/**
 * to build MMM - MMM yyyy period label for Rolling Period
 * if Periodicity/timeFrame is Rolling Quarter then build new label using above format
 * Rec
 */

export const getNewPeriodicityOptionsLabel = (periodicity: Periodicity, period: string): string => {
  if (periodicity === Periodicity.RollingQuarter) {
    return period.substring(0, 3) + period.substring(8);
  }

  return period;
};

//transform label option in periodOptions array before passing to radioButtons DS component
export const transformPeriodOptions = (periodOptions: BaseTimePeriod[], selectedPeriodicity: Periodicity) => {
  return periodOptions.map((option) => ({
    ...option,
    label: getNewPeriodicityOptionsLabel(selectedPeriodicity, option.label),
  }));
};

/**
 * Use to convert periodicity to monthly for custom Perdiocity selections.
 * This is due, only monthly and weekly timeframe available in Product Group selections.
 * Also, Line Chart only support weekly ad monthly periodicity.
 * Therefore, derivable periodicities such as Rolling Quarter, Calendar Quarter and Custom Month need to be return as Monthly to be able to filter correct PG
 * @param periodicity - periodicity selected in Time Frame Picker.
 * Returns monthly periodicity for selected timeFrame equals Rolling Quarter, Calendar Quarter or Custom Month
 */
export const getDefaultValueForDerivablePeriodicity = (periodicity: Periodicity) => {
  if ([Periodicity.RollingQuarter, Periodicity.CalendarQuarter, Periodicity.CustomMonths].includes(periodicity)) {
    return Periodicity.Monthly;
  }
  if (periodicity === Periodicity.CustomWeeks) {
    return Periodicity.Weekly;
  }
  return periodicity;
};

/**
 *
 * @param date as string MMM yyyy
 * @param lastUpdatedDate as last updated date from the period options
 * @param monthToSubtract for backdated period for custom month. Put 1 to create default comparison period and 0 for default period
 */
export const buildDefaultCustomMonthPeriodWithString: (
  date: string,
  lastUpdatedDate: number,
  monthToSubtract: number,
) => BaseTimePeriod = (date, lastUpdatedDate, monthToSubtract) => {
  const backDatedPeriod = getPreviousMonth(getDateFromPeriod(date), monthToSubtract);
  const rangedComparisonPeriod = `${formatMMMyyyy(backDatedPeriod)} - ${formatMMMyyyy(backDatedPeriod)}`;
  return {
    label: rangedComparisonPeriod,
    value: rangedComparisonPeriod,
    lastUpdatedDate: lastUpdatedDate,
  };
};

export const buildDefaultCustomWeekPeriodWithString: (
  date: string,
  lastUpdatedDate: number,
  weeksToSubtract: number,
) => BaseTimePeriod = (date, lastUpdatedDate, weeksToSubtract) => {
  const backDatedPeriod = getPreviousWeek(getDateFromPeriod(date), weeksToSubtract);
  const rangedComparisonPeriod = `${formatWwyyyy(backDatedPeriod)} - ${formatWwyyyy(backDatedPeriod)}`;
  return {
    label: rangedComparisonPeriod,
    value: rangedComparisonPeriod,
    lastUpdatedDate: lastUpdatedDate,
  };
};

/**
 * Use to format Date format to string date in the form of MMM yyyy
 * @param date in the form of Date
 * Returns string date in the format of MMM yyyy
 */
export const formatMMMyyyy = (date: Date) => {
  return format(date, 'MMM yyyy');
};

/**
 * Use to format Date format to string date in the form of Ww yyyy
 * @param date in the form of Date
 * Returns string date in the format of Ww yyyy
 */
const formatWwyyyy = (date: Date) => {
  const weekNumber = getWeek(date, { weekStartsOn: 0, firstWeekContainsDate: 1 });
  const adjustedWeekYear = getWeekYear(date, { weekStartsOn: 0, firstWeekContainsDate: 1 });

  return `W${weekNumber} ${adjustedWeekYear}`;
};

/**
 * Use to format selectedPeriod and selectedComparisonPeriod
 * Mostly used for custom months or custom weeks
 * @param dateFrom - earliest date
 * @param dateTo - latest date
 * @param lastUpdatedDate - lastUpdateddate from the timePeriod Options
 * @param isWeek - if custom weeks
 * Returns range of date
 */
export const formatRangeBaseTimePeriods = (dateFrom: Date, dateTo: Date, lastUpdatedDate: number, isWeek?: boolean) => {
  const rangeValue = isWeek
    ? `${formatWwyyyy(dateFrom)} - ${formatWwyyyy(dateTo)}`
    : `${formatMMMyyyy(dateFrom)} - ${formatMMMyyyy(dateTo)}`;
  return {
    label: rangeValue,
    value: rangeValue,
    lastUpdatedDate: lastUpdatedDate,
  };
};

/**
 *
 * @param period - the date passed in Date format
 * @param monthsToSubtract - number of months to be subtracted
 */
export const getPreviousMonth: (period: Date, monthsToSubtract: number) => Date = (period, monthsToSubtract) => {
  return subMonths(period, monthsToSubtract);
};

export const getPreviousWeek = (date: Date, weeksToSubtract: number): Date => {
  const daysToSubtract = weeksToSubtract * 7;
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() - daysToSubtract);
  return newDate;
};

/**
 * Converts a `BaseTimePeriod` to a `Date` object.
 *
 * @param period - A reference to the period object, where its label is in MMM yyy format (e.g. Oct 2023)
 */
export const baseTimePeriodToDate: (period: BaseTimePeriod) => Date = (period) => {
  return getDateFromPeriod(period.label);
};

/**
 * check whether if its casted to BaseTimePeriod, the label exist
 * if not exist means is in a RelativePeriod format
 * Currently type guard is not applicable, since RelativePeriod is an enum, and using 'in' is not applicable.
 * @param comparisonPeriods will be passed either BaseTimePeriod or RelativePeriod format
 */
export const isBaseTimePeriodFormat = (comparisonPeriods: RelativePeriod | BaseTimePeriod): boolean => {
  const baseTimePeriodFormat = comparisonPeriods as BaseTimePeriod;

  //if baseTimePeriodFormat.label is undefined, then will return false
  //currently only label checking is enough to make sure its a BaseTimePeriod format
  return !!baseTimePeriodFormat.label;
};

/**
 * Returns a formatted focus period label to be used for amplitude tracking.
 *
 * @param periodicity The currently selected periodicity (e.g. Monthly)
 * @param periodLabel The currently selected period label (e.g. Jan 2021)
 */
export const formatFocusPeriodForTracking = (periodicity: Periodicity, periodLabel: string): string => {
  if (periodicity === Periodicity.CalendarQuarter) {
    return addStartingYearForCalendarQuarter(periodLabel);
  }

  return periodLabel;
};

/**
 * Dispatches actions to update the main filters' period (redux) and periodicity based on chosen values.
 *
 * @param periodDispatchActions Containing update functions for period-related actions.
 * @param chosenValues Chosen values including chosenTimeFrame and chosenTimePeriod.
 */
export const dispatchUpdateMainFiltersPeriod = (
  periodDispatchActions:
    | {
        updatePeriod: (period: BaseTimePeriod) => void;
        updatePeriodicity: (periodicity: Periodicity) => void;
        updateComparisonPeriod: (comparisonPeriod: RelativePeriod | BaseTimePeriod) => void;
      }
    | undefined,
  chosenValues: { chosenTimeFrame: string; chosenTimePeriod: any },
) => {
  if (periodDispatchActions) {
    periodDispatchActions.updatePeriod(chosenValues.chosenTimePeriod);
    periodDispatchActions.updatePeriodicity(
      chosenValues.chosenTimeFrame.toLowerCase() === 'weekly' ? Periodicity.Weekly : Periodicity.Monthly,
    );
    periodDispatchActions.updateComparisonPeriod(RelativePeriod.PreviousYearCurrentPeriod);
  }
};
/**
 * Used to build single BaseTimePeriod (focus period or comparison period)
 *
 * @param customMonthsPeriod
 * @param monthsToSubtract - the value of month to be subtract from the current latest period
 * For example, for latest period July 2023, 0 month to be subtracted, while comparison period 1 months to be subtracted
 */
export const constructDefaultCustomMonthsLatestPeriod = (
  customMonthsPeriod: BaseTimePeriod,
  monthsToSubtract: number,
) => {
  return buildDefaultCustomMonthPeriodWithString(
    customMonthsPeriod.label,
    customMonthsPeriod.lastUpdatedDate,
    monthsToSubtract,
  );
};

/**
 * Customize utils to build default Custom Months selections
 * Consist of period and comparison selection.
 * To be used throughout code, mainly used in UseInitializedPeriodSet to initialize Default Custom Months selection during render page.
 * @param timePeriod - consist of all available period options for retailer
 * Passing the whole timePeriod to check undefined/null value of each timeperiod level down to the specific custom months options.
 */
export const getDefaultCustomMonthsPeriod = (timePeriod: TimePeriod) => {
  return (
    timePeriod.customMonths[0] && {
      period: constructDefaultCustomMonthsLatestPeriod(timePeriod.customMonths[0], 0),
      comparisonPeriod: constructDefaultCustomMonthsLatestPeriod(timePeriod.customMonths[0], 1),
    }
  );
};

export const constructDefaultCustomWeeksLatestPeriod = (customWeeksPeriod: BaseTimePeriod, weeksToSubtract: number) => {
  return buildDefaultCustomWeekPeriodWithString(
    customWeeksPeriod.label,
    customWeeksPeriod.lastUpdatedDate,
    weeksToSubtract,
  );
};

/**
 * Customize utils to build default Custom Weeks selections
 * Consist of period and comparison selection.
 * To be used throughout code, mainly used in UseInitializedPeriodSet to initialize Default Custom Weeks selection during render page.
 * @param timePeriod - consist of all available period options for retailer
 * Passing the whole timePeriod to check undefined/null value of each timeperiod level down to the specific custom weeks options.
 */
export const getDefaultCustomWeeksPeriod = (timePeriod: TimePeriod) => {
  return (
    timePeriod.customWeeks[0] && {
      period: constructDefaultCustomWeeksLatestPeriod(timePeriod.customWeeks[0], 0),
      comparisonPeriod: constructDefaultCustomWeeksLatestPeriod(timePeriod.customWeeks[0], 1),
    }
  );
};

/**
 * Used to check the validity of custom months/weeks periods selection from Redux
 * If the redux value for custom months/weeks are still within allowed selectable range for the Retailer, then retain it
 * Else it will return false and custom months/weeks selection will be set with default value
 * @param customPeriod
 * @param customPeriodOptions - the custom months/weeks timePeriod from Filter/API
 */
export const isValidCustomPeriodSelections = (
  customPeriod: CustomMonthsPeriodState | CustomWeeksPeriodState | null | undefined,
  customPeriodOptions: BaseTimePeriod[],
): boolean => {
  if (!customPeriod?.period || !customPeriod?.comparisonPeriod || customPeriodOptions?.length === 0) {
    return false;
  }

  const [startDateFocusPeriod, endDateFocusPeriod] = customPeriod.period.label.split(' - ');
  const [startDateComparisonPeriod, endDateComparisonPeriod] = customPeriod.comparisonPeriod.label.split(' - ');
  const latestPeriod = customPeriodOptions[0].label;
  const earliestPeriod = customPeriodOptions[1].label;

  const isValidPeriod = (start: string, end: string) =>
    isSelectedRangeMoreThanMinLimit(earliestPeriod, start) &&
    isSelectedRangeUnderMaxLimit(latestPeriod, start) &&
    isSelectedRangeMoreThanMinLimit(earliestPeriod, end) &&
    isSelectedRangeUnderMaxLimit(latestPeriod, end);

  return (
    isValidPeriod(startDateFocusPeriod, endDateFocusPeriod) &&
    isValidPeriod(startDateComparisonPeriod, endDateComparisonPeriod)
  );
};

/**
 * Determines whether the selected period is within the maximum limit period.
 *
 * @param maxLimitPeriodLabel - The default latest period in MMM yyy format (e.g. Oct 2023)
 * @param selectedPeriodLabel - The selected date in MMM yyy format (e.g. Sep 2023)
 */
const isSelectedRangeUnderMaxLimit = (maxLimitPeriodLabel: string, selectedPeriodLabel: string) => {
  const maxLimitDate = getDateFromPeriod(maxLimitPeriodLabel);
  const selectedDate = getDateFromPeriod(selectedPeriodLabel);

  return maxLimitDate >= selectedDate;
};

/**
 * Determines if the selected period range is greater than or equal to the minimum limit period
 * range.
 *
 * @param minLimitPeriodLabel - The default earliest period in MMM yyy format (e.g. Oct 2023)
 * @param selectedPeriodLabel - The selected date in MMM yyy format (e.g. Sep 2023)
 */
const isSelectedRangeMoreThanMinLimit = (minLimitPeriodLabel: string, selectedPeriodLabel: string) => {
  const minLimitDate = getDateFromPeriod(minLimitPeriodLabel);
  const selectedDate = getDateFromPeriod(selectedPeriodLabel);

  return minLimitDate <= selectedDate;
};

/**
 * To check whether to disable comparison period selection in TimeFramePicker
 * To change default comparison period to Previous Period if period selection is not latest option
 * Temporarily used for Products leaderboard page to disable comparison period for previous year current period
 * for back periods (not latest period selection as product contain only 13 monthly period)
 * @param period is the current period selection
 * @param latestPeriod is the current latest period for the chosen periodicity
 * @param setIsDisableComparisonPeriod setter which update the state value for isDisableComparisonPeriod in TimeFramePicker
 * @param setSelectedComparison setter which update the state value for selectedComparison in TimeFramePicker if needed
 */

export const disabledComparisonPeriodForProducts = (
  period: BaseTimePeriod,
  latestPeriod: BaseTimePeriod,
  setIsDisableComparisonPeriod: Dispatch<SetStateAction<boolean>>,
  setSelectedComparison: Dispatch<SetStateAction<BaseTimePeriod | RelativePeriod>>,
) => {
  const isSelectionLatestPeriod = period.label !== latestPeriod.label;
  if (isSelectionLatestPeriod) {
    setSelectedComparison(RelativePeriod.PreviousPeriod);
  }
  setIsDisableComparisonPeriod(isSelectionLatestPeriod);
};

/**
 * To handle the MAT and YTD periodicity in saving view
 */
export const getPeriodicityForMATAndYTD = (period: BaseTimePeriod, periodicity: Periodicity) => {
  if (period.label.includes(PeriodicityKey.YTD)) {
    return {
      periodicity: Periodicity.YTD,
      matYtdLabel: period.label,
    };
  } else if (period.label.includes(PeriodicityKey.MAT)) {
    return {
      periodicity: Periodicity.MAT,
      matYtdLabel: period.label,
    };
  }

  return {
    periodicity: periodicity,
    matYtdLabel: '',
  };
};

export const isPeriodicityMATOrYTD = (periodicity: Periodicity) => {
  return periodicity === Periodicity.MAT || periodicity === Periodicity.YTD;
};

export const isWeeklyYTDOrMAT = (periodicity: Periodicity, period: BaseTimePeriod) => {
  return periodicity === Periodicity.Weekly && isPeriodYTDOrMAT(period);
};

/**
 * Returns true if periodicity is CustomWeeks
 * @param periodicity
 */
export const isCustomWeeks = (periodicity: Periodicity) => {
  return periodicity === Periodicity.CustomWeeks;
};

export const isPeriodLabelYTDOrMAT = (period: BaseTimePeriod, length: number) => {
  return period.label.includes(YTD) || (period.label.includes(MAT) && length > 1);
};

export const isPeriodValueYTDOrMAT = (periodValue: string) => {
  return periodValue.includes('YTD') || periodValue.includes('MAT');
};

/**
 * Checks whether the period value is in V2 or V3 data format by using strict equality
 * since V2 data format for YTD and MAT does not include the date range.
 * Eg: V2 format - 'MAT'
 * V3 format - 'MAT Jan 2024 - Dec 2024
 * @param periodValue The period label
 * @returns boolean value
 */
export const isPeriodValueV2YTDOrMAT = (periodValue: string): boolean => {
  return periodValue === 'YTD' || periodValue === 'MAT';
};

export const getMainFiltersList = (period: BaseTimePeriod, defaultFilters: MainFilters[], countryList: string[]) => {
  return period.label.includes(WEEK)
    ? defaultFilters.filter((e) => countryList.includes(e.country) && e.timePeriod.weekly.length > 0)
    : defaultFilters.filter((e) => countryList.includes(e.country) && e.timePeriod.monthly.length > 0);
};

export const monthlyWeeklyToggle = (periodicity: Periodicity) => {
  if (
    [Periodicity.Monthly, Periodicity.RollingQuarter, Periodicity.CalendarQuarter, Periodicity.CustomMonths].includes(
      periodicity,
    )
  )
    return Periodicity.Weekly;
  if (periodicity === Periodicity.Weekly) return Periodicity.Monthly;
  else return periodicity;
};

export const getPeriodsWithoutYtdMat = (periods: BaseTimePeriod[]) =>
  periods.filter((period) => !isPeriodYTDOrMAT(period));

export const isEligiblePeriodicityForWarning = (periodicity: string, periodValue: string): boolean => {
  return (
    (periodicity === Periodicity.Monthly ||
      periodicity === Periodicity.Weekly ||
      periodicity === Periodicity.RollingQuarter ||
      periodicity === Periodicity.CalendarQuarter ||
      periodicity === Periodicity.CustomMonths) &&
    periodValue !== PeriodicityKey.YTD &&
    periodValue !== PeriodicityKey.MAT
  );
};

export const convertYTDOrMATtoPeriodType = (periodicity: Periodicity, periodLabel: string) => {
  if (periodicity === Periodicity.YTD || periodicity === Periodicity.MAT) {
    return periodLabel.includes(WEEK) ? Periodicity.Weekly : Periodicity.Monthly;
  }

  return periodicity;
};

export const getPeriodValueByTimeFrame = (timeFrame: Periodicity, periodValue: string) => {
  switch (timeFrame) {
    case Periodicity.YTD:
    case Periodicity.MAT:
    case Periodicity.Monthly:
    case Periodicity.Weekly:
      if (periodValue.includes(MAT) || periodValue.includes(YTD)) {
        if (periodValue.includes(WEEK)) {
          let weekStr = periodValue.split(' ');
          //to handle for single week which is single digit
          weekStr[6] = weekStr[6].length > 1 ? weekStr[6] : `0${weekStr[6]}`;
          return `${weekStr[5]} ${weekStr[6]} ${weekStr[7]}`;
        } else {
          return periodValue.slice(15, 23);
        }
      }
      return periodValue;
    case Periodicity.CalendarQuarter:
    case Periodicity.CustomMonths:
    case Periodicity.RollingQuarter: {
      // Calendar Quarter will have the following format: Jan - Mar 2024
      // Custom Months and Rolling Quarter will have the following format: Jan 2024 - Mar 2024
      const startingAndEndingPeriods = periodValue.split(' - ');
      return startingAndEndingPeriods[startingAndEndingPeriods.length - 1];
    }
    case Periodicity.CustomWeeks: {
      // Custom Weeks will have the following format: W18 2025 - W18 2025
      const startingAndEndingPeriods = periodValue.split(' - ');
      const weekStr = startingAndEndingPeriods[startingAndEndingPeriods.length - 1];
      return isWeekFormat?.(weekStr) ? weekStr.replace(/^W/, 'Week ') : weekStr;
    }
    default:
      return periodValue;
  }
};

export const checkPeriodValueAndSliceForMATYTD = (periodValue: string) => {
  if (periodValue.includes('MAT') || periodValue.includes('YTD')) {
    return periodValue.slice(4);
  }

  return periodValue;
};

export const removeYTDMATPeriodLabel = (period: string | undefined) => {
  return period !== undefined ? period.replace(/ytd/i, '').replace(/mat/i, '').trim() : undefined;
};

/**
 * Retrieves the end period of the ranged period value.
 * If a non ranged period is passed in, it will return the original value
 * @param periodValue - Ranged period value. Eg: 'Jan 2023 - Dec 2023'
 * @param periodicity - Periodicity (Eg: Monthly, Weekly)
 * @returns the end period of the ranged period. Eg: 'Dec 2023'
 */
export const getEndOfRangedPeriod = (periodValue: string, periodicity: Periodicity): string => {
  const formattedPeriodValue = periodValue.includes('-') ? periodValue.split('-')[1]?.trim() : periodValue;
  if (periodicity === Periodicity.Weekly) {
    const weekPeriodTokens = formattedPeriodValue.split(' ');
    weekPeriodTokens[1] = weekPeriodTokens[1]?.padStart(2, '0') ?? '';
    return weekPeriodTokens.join(' ');
  }
  return formattedPeriodValue;
};

/**
 *Case handling for Weekly MAT/YTD,
 since we need the periodicity to identify whether it is Weekly YTD/MAT
 * @param period
 * @param periodicity
 * @returns
 */
export const getBaseTimePeriodForMATAndYTD = (period: string, periodicity: Periodicity): BaseTimePeriod => {
  if (isPeriodValueYTDOrMAT(period) && !period.toLowerCase().includes('week') && periodicity === Periodicity.Weekly) {
    return {
      label: `${period} ${periodicity}`,
      value: period,
      lastUpdatedDate: 0,
    };
  }
  return {
    label: period,
    value: period,
    lastUpdatedDate: 0,
  };
};

/***
 * Since for derivable periodicities e.g. YTD, MAT, Custom months, rolling quarter, calendar quarter is a derived range periodicities,
 * therefore, weekly/monthly 13 rolling periods on Line chart need to be taken directly from filterAPI response on weekly/monthly option.
 * Latest period for monthly will be last 3 from the timePeriodOptions.monthly/weekly, with YTS and MAt at the last 2
 * So slicing -3 will get index 3 from the back.
 */
export const getDefaultMonthlyPeriodForPALineChart = (
  periodicity: Periodicity,
  period: BaseTimePeriod,
  timePeriodOptions: TimePeriod,
): BaseTimePeriod => {
  const defaultPeriodicityWithoutYTDMAT = convertYTDOrMATtoPeriodType(
    period.label.includes(WEEK) ? Periodicity.Weekly : getDefaultValueForDerivablePeriodicity(periodicity),
    period.label,
  );

  const getLatestPeriodWithoutMATYTD = (periods: BaseTimePeriod[]): BaseTimePeriod | undefined => {
    if (!periods || periods.length === 0) return;
    const filteredPeriods = periods.filter((p) => !isPeriodYTDOrMAT(p));
    return filteredPeriods[filteredPeriods.length - 1];
  };

  if (defaultPeriodicityWithoutYTDMAT.includes(Periodicity.Weekly) && timePeriodOptions.weekly?.length) {
    return period || getLatestPeriodWithoutMATYTD(timePeriodOptions.weekly);
  } else if (defaultPeriodicityWithoutYTDMAT.includes(Periodicity.Monthly) && timePeriodOptions.monthly?.length) {
    return period || getLatestPeriodWithoutMATYTD(timePeriodOptions.monthly);
  }

  return period;
};

export const formatPeriodType: (periodicity: string) => string = (periodicity) => {
  const periodTypeParam: string =
    Periodicity.CalendarQuarter === periodicity || Periodicity.RollingQuarter === periodicity
      ? Periodicity.Monthly
      : periodicity;

  return periodTypeParam.toUpperCase();
};

/**
 * aggregatedPeriodicity is the periodicity derived from Monthly and Weekly
 * Therefore, YTD, MAT, Rolling Quarter, Calendar Quarter and Custom Months falls under this category
 * periodicity - the current periodicity selected by user
 * return boolean true or false
 * if periodicity is monthly or weekly, then we dont need to show warning on line chart.
 */
export const isAggregatedPeriodicity = (periodicity: Periodicity, period: string) => {
  //currently, for YTD and MAT, the periodicity is default to Monthly or Weekly for export usage.
  //Therefore it will not valid for excludedPeriodicityForLineChart checking
  //So current checking for YTD and MAT is using period value either contain YTD/MAT value.
  //For example YTD Jan 2024 - Jun 2024
  //Clean up need to be done later once the requirements changed for export excel, png or pptx
  return excludedPeriodicityForLineChart.includes(periodicity) || period.includes('YTD') || period.includes('MAT');
};

/**
 * Checks whether the current timeframe is a custom periodicity.
 *
 * @return boolean true or false
 */
export const isCustomPeriodTimeFrame = (periodicity: Periodicity): boolean => {
  return periodicity && [Periodicity.CustomMonths, Periodicity.CustomWeeks].includes(periodicity);
};

/**
 * Constain the periodicity excluded for Line Chart
 * Only Monthly and Weekly are allowed for focus period selection in Line Chart timeframepicker
 */
export const excludedPeriodicityForLineChart = [
  Periodicity.YTD,
  Periodicity.MAT,
  Periodicity.RollingQuarter,
  Periodicity.CalendarQuarter,
  Periodicity.CustomMonths,
  Periodicity.CustomWeeks,
];
